/**
 * Per-status copy for the form error card.
 *
 * `/api/form-submit` rejects a submission for four distinct reasons and redirects
 * back with `?error=<status>`. Every status used to render one generic "Verification
 * Failed — We couldn't verify your submission" card, which is actively misleading for
 * a work-email rejection: it reads as a bot-check or network problem, so a genuine
 * prospect presses Try Again with the same address and fails forever (#3426).
 *
 * Copy rules: plain and non-blaming — no exclamation marks, no "oops", and never
 * imply the user did something wrong.
 */
export type FormErrorStatus =
  | 'work-email-required'
  | 'verification-required'
  | 'verification-failed'
  | 'submission-failed';

export interface FormErrorMessage {
  title: string;
  body: string;
  /**
   * Whether the fix lives in the email field. When true the card's "Try Again" returns
   * to the form and moves focus to the email input (the one field the user must change)
   * — see FOCUS_EMAIL_HASH.
   */
  focusEmail: boolean;
}

/**
 * Hash appended to "Try Again" for email-fixable rejections. Deliberately NOT the
 * existing `#hero-form` anchor: that one already drives the trial page's sticky-CTA
 * focus, so reusing it would fire that behaviour on unrelated forms.
 */
export const FOCUS_EMAIL_HASH = '#work-email';

const FORM_ERROR_MESSAGES: Record<FormErrorStatus, FormErrorMessage> = {
  // Server-side work-email blocklist. NOT a verification failure — say what is wrong
  // with the address, because that is the only thing a retry can fix.
  'work-email-required': {
    title: 'Work email required',
    body: "Please try again using your work or company email address. We can't accept free or personal email addresses.",
    focusEmail: true,
  },
  // Turnstile token absent — the widget never produced one (blocked, expired, or JS off).
  'verification-required': {
    title: 'Verification needed',
    body: "We couldn't verify your submission because the verification check didn't complete. Please try again.",
    focusEmail: false,
  },
  // Turnstile token present but rejected by Cloudflare.
  'verification-failed': {
    title: 'Verification failed',
    body: "We couldn't verify your submission. Please try again.",
    focusEmail: false,
  },
  // Our proxy reached Pardot and Pardot refused, or the request threw.
  'submission-failed': {
    title: 'Something went wrong',
    body: "Something went wrong on our end and your request wasn't sent. Please try again.",
    focusEmail: false,
  },
};

/**
 * An in-page submit got no answer at all (the visitor's connection dropped). The request
 * may still have reached us, so don't say it wasn't sent.
 */
export const CONNECTION_ERROR_MESSAGE: FormErrorMessage = {
  title: 'Connection problem',
  body: "We couldn't confirm your request was sent. Please check your connection and try again.",
  focusEmail: false,
};

/**
 * Resolve `?error=<status>` to its card copy. Anything unrecognised (or a repeated
 * param, which arrives as an array) falls back to the generic server-side failure —
 * never to a verification message, which would be a guess about the cause.
 */
export function resolveFormErrorMessage(status: string | string[] | undefined): FormErrorMessage {
  if (typeof status === 'string' && status in FORM_ERROR_MESSAGES) {
    return FORM_ERROR_MESSAGES[status as FormErrorStatus];
  }
  return FORM_ERROR_MESSAGES['submission-failed'];
}

/** "Try Again" target: returns to the form, focusing the email field when that is the fix. */
export function buildTryAgainHref(basePath: string, message: FormErrorMessage): string {
  return message.focusEmail ? `${basePath}${FOCUS_EMAIL_HASH}` : basePath;
}
